/**
 * Helpers for components that position themselves from layout measurements while the
 * page is under a CSS `zoom` (an app-level zoom control, for instance).
 *
 * `getBoundingClientRect()`, `clientX/clientY` and `window.innerWidth/innerHeight` all
 * report the *visual* geometry — zoom already applied. A px value written to an
 * element's own `style`, though, is resolved in that element's own coordinate space,
 * which zoom scales again on the way out. Feeding a measurement straight back as a px
 * offset therefore lands it at `value * zoom`: at 80% a popup anchored to an input
 * 500px from the left edge renders at 400px.
 *
 * So: divide every measurement by the zoom factor of the element being positioned, do
 * the arithmetic in that local space, and write the result unmodified.
 */

/** A `getBoundingClientRect()` result converted to some element's local coordinate space. */
export interface ZoomedRect {
    top: number;
    right: number;
    bottom: number;
    left: number;
    width: number;
    height: number;
}

/**
 * The effective CSS zoom applied to `element` (the product of its own `zoom` and every
 * ancestor's). Returns 1 when nothing is zoomed, server-side, or for a detached element.
 * Pass no element to read the document root, which is where an app-level zoom lives.
 */
export function cssZoom(element?: Element | null): number {
    if (typeof window === 'undefined' || typeof document === 'undefined') return 1;

    const target = element ?? document.documentElement;
    // currentCSSZoom is the accumulated zoom of the element and its ancestors.
    const accumulated = (target as Element & { currentCSSZoom?: number }).currentCSSZoom;
    if (typeof accumulated === 'number' && accumulated > 0) return accumulated;

    // Browsers that support `zoom` but not currentCSSZoom: we only ever zoom the root,
    // so reading it there is equivalent. getPropertyValue rather than `.zoom` because
    // the property is missing from some CSSStyleDeclaration typings.
    const rootZoom = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('zoom'));
    return Number.isFinite(rootZoom) && rootZoom > 0 ? rootZoom : 1;
}

/**
 * `source`'s bounding rect, expressed in the local coordinate space of `positioned` —
 * the element whose inline `style` the numbers are destined for.
 */
export function zoomedRect(source: Element, positioned?: Element | null): ZoomedRect {
    const rect = source.getBoundingClientRect();
    const zoom = cssZoom(positioned);
    if (zoom === 1) return rect;
    return {
        top: rect.top / zoom,
        right: rect.right / zoom,
        bottom: rect.bottom / zoom,
        left: rect.left / zoom,
        width: rect.width / zoom,
        height: rect.height / zoom
    };
}

/** The viewport size in the local coordinate space of `positioned`. */
export function zoomedViewport(positioned?: Element | null): { width: number; height: number } {
    const zoom = cssZoom(positioned);
    return { width: window.innerWidth / zoom, height: window.innerHeight / zoom };
}
